import { Drug } from '../types';

export type { Drug };

export interface Category {
  id: string;
  name: string;
  color: string;
}

export const categories: Category[] = [
  { id: 'cannabis', name: 'Cannabis', color: 'bg-emerald-600' },
  { id: 'stimulants', name: 'Stimulants', color: 'bg-red-500' },
  { id: 'psychedelics', name: 'Psychedelics', color: 'bg-violet-500' },
  { id: 'depressants', name: 'Depressants', color: 'bg-blue-600' },
  { id: 'dissociatives', name: 'Dissociatives', color: 'bg-teal-500' },
  { id: 'entactogens', name: 'Entactogens', color: 'bg-pink-600' },
  { id: 'other', name: 'Other', color: 'bg-gray-700' },
];

// Assign different color variants to each drug based on their category
export const drugColorVariants: Record<string, string> = {
  // Cannabis - emerald variants
  'cannabis': 'bg-emerald-600',
  
  // Stimulants - red variants
  'amphetamine': 'bg-red-600',
  'cocaine': 'bg-red-500',
  '3mmc': 'bg-red-400',
  'caffeine': 'bg-red-300',
  
  // Psychedelics - violet variants
  'lsd': 'bg-violet-400',
  'psilocybin': 'bg-violet-500',
  'dmt': 'bg-violet-700',
  'changa': 'bg-violet-600',
  '2cb': 'bg-violet-300',
  
  // Depressants - blue variants
  'alcohol': 'bg-blue-500',
  'ghb': 'bg-blue-600',
  
  // Dissociatives - teal variants
  'ketamine': 'bg-teal-500',
  
  // Entactogens - pink variants
  'mdma': 'bg-pink-500',
  'ecstasy': 'bg-pink-600',
};

// Helper to get the color for a specific drug
export const getDrugColor = (drugId: string): string => {
  // If the drug has a specific color variant, use it
  if (drugId in drugColorVariants) {
    return drugColorVariants[drugId];
  }
  
  // Otherwise find the drug and use its category color
  const drug = getDrugById(drugId);
  if (drug) {
    const category = categories.find(c => c.id === drug.category);
    return category?.color || 'bg-gray-700';
  }
  
  return 'bg-gray-700'; // Default fallback
};

// Initialize drugs with default list
export const drugs: Drug[] = [
  {
    id: 'alcohol',
    name: 'Alcohol',
    category: 'depressants',
    defaultUnit: 'ml',
    description: 'Central nervous system depressant',
    onsetTime: 15,
    peakTime: 60,
    duration: 240,
  },
  {
    id: 'cannabis_joint',
    name: 'Joint',
    category: 'cannabis',
    defaultUnit: 'joint',
    description: 'Smoked cannabis rolled in paper',
    onsetTime: 15, // Smoked
    peakTime: 30, // Smoked
    duration: 180, // Smoked
  },
  {
    id: 'cannabis_oil',
    name: 'Oil',
    category: 'cannabis',
    defaultUnit: 'ml',
    description: 'Cannabis extract consumed orally',
    onsetTime: 90, // Adjusted for oral
    peakTime: 210, // Adjusted for oral
    duration: 480, // Adjusted for oral
  },
  {
    id: 'cannabis_bud',
    name: 'Bud',
    category: 'cannabis',
    defaultUnit: 'g',
    description: 'Dried cannabis flower (smoked/vaped)',
    onsetTime: 15, // Smoked/Vaped
    peakTime: 30, // Smoked/Vaped
    duration: 180, // Smoked/Vaped
  },
  {
    id: 'lsd',
    name: 'LSD',
    category: 'psychedelics',
    defaultUnit: 'μg',
    description: 'Classic psychedelic substance',
    onsetTime: 45,
    peakTime: 180,
    duration: 720,
  },
  {
    id: 'psilocybin',
    name: 'Psilocybin (mushrooms)',
    category: 'psychedelics',
    defaultUnit: 'g',
    description: 'Natural psychedelic substance',
    onsetTime: 30,
    peakTime: 120,
    duration: 360,
  },
  {
    id: 'dmt',
    name: 'DMT',
    category: 'psychedelics',
    defaultUnit: 'mg',
    description: 'Powerful, short-acting psychedelic compound',
    onsetTime: 1,
    peakTime: 5,
    duration: 30,
  },
  {
    id: 'changa',
    name: 'Changa',
    category: 'psychedelics',
    defaultUnit: 'mg',
    description: 'DMT-infused smoking blend with MAOIs',
    onsetTime: 1,
    peakTime: 10,
    duration: 45,
  },
  {
    id: '2cb',
    name: '2C-B',
    category: 'psychedelics',
    defaultUnit: 'mg',
    description: 'Psychedelic phenethylamine with mild entactogenic effects',
    onsetTime: 45,
    peakTime: 120,
    duration: 360,
  },
  {
    id: 'mdma',
    name: 'MDMA',
    category: 'entactogens',
    defaultUnit: 'mg',
    description: 'Entactogen with stimulant properties',
    onsetTime: 30,
    peakTime: 90,
    duration: 360,
  },
  {
    id: 'ecstasy',
    name: 'Ecstasy pill',
    category: 'entactogens',
    defaultUnit: 'pill',
    description: 'Pressed pill containing MDMA and potentially other substances',
    onsetTime: 30,
    peakTime: 90,
    duration: 360,
  },
  {
    id: 'ketamine',
    name: 'Ketamine',
    category: 'dissociatives',
    defaultUnit: 'mg',
    description: 'Dissociative anesthetic',
    onsetTime: 15,
    peakTime: 60,
    duration: 180,
  },
  {
    id: 'ghb',
    name: 'GBL',
    category: 'depressants',
    defaultUnit: 'ml',
    description: 'Central nervous system depressant with euphoric effects',
    onsetTime: 15,
    peakTime: 60,
    duration: 240,
  },
  {
    id: 'amphetamine',
    name: 'Amphetamine',
    category: 'stimulants',
    defaultUnit: 'mg',
    description: 'Central nervous system stimulant',
    onsetTime: 20,
    peakTime: 90,
    duration: 360,
  },
  {
    id: 'cocaine',
    name: 'Cocaine',
    category: 'stimulants',
    defaultUnit: 'mg',
    description: 'Powerful stimulant with short duration',
    onsetTime: 5,
    peakTime: 30,
    duration: 60,
  },
  {
    id: '3mmc',
    name: '3MMC',
    category: 'stimulants',
    defaultUnit: 'mg',
    description: 'Synthetic cathinone stimulant',
    onsetTime: 15,
    peakTime: 60,
    duration: 240,
  },
  {
    id: 'caffeine',
    name: 'Caffeine',
    category: 'stimulants',
    defaultUnit: 'mg',
    description: 'Common stimulant found in coffee, tea, and energy drinks',
    onsetTime: 15,
    peakTime: 45,
    duration: 240,
  },
];

// Helper functions that work with any drugs list
export const getDrugById = (id: string, drugsList: Drug[] = drugs): Drug | undefined => {
  return drugsList.find(drug => drug.id === id);
};

export const getDrugsByCategory = (categoryId: string, drugsList: Drug[] = drugs): Drug[] => {
  return drugsList.filter(drug => drug.category === categoryId);
}; 